import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { PageHeader } from '@/components/common/PageHeader'
import { AccountStatusChip, PermissionChip, EntityChip } from '@/components/common/StatusChips'
import { useAuth } from '@/auth/useAuth'
import { displayName, formatDate } from '@/lib/format'

export default function DashboardPage() {
  const { user, permissions, groups } = useAuth()
  if (!user) return null

  const firstName = displayName(user).split(' ')[0]

  return (
    <>
      <PageHeader title={`Welcome back, ${firstName}`} subtitle={user.statusMessage} />

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr' } }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Account
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <EntityChip entity={user.entity} size="medium" />
              <AccountStatusChip status={user.status} size="medium" />
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              <Detail label="Email" value={user.email} />
              <Detail label="Position" value={user.position ?? '—'} />
              <Detail label="Division" value={user.division ?? '—'} />
              <Detail label="Member since" value={formatDate(user.createdAt)} />
              <Detail label="Groups" value={groups.length > 0 ? groups.join(', ') : 'No groups assigned'} />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Your access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Navigation and actions are shown only for the permissions your groups grant.
            </Typography>
            {permissions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No permissions granted yet. Contact an administrator.
              </Typography>
            ) : (
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {permissions.map((code) => (
                  <PermissionChip key={code} code={code} />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  )
}
